import { UserResource } from '@aorg/models/resources/UserResource';
import { selectSession, useAppSelector } from '@aorg/store';
import {
  AnalyticsClient,
  AnalyticsHook,
  ClickProperties,
  EventProperties,
  PageViewProperties,
} from './types';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

export interface AnalyticsFactoryConfig {
  client: AnalyticsClient;
  resolveApiKey: () => string | undefined;
  resolveEnv: () => string | undefined;
  platform: 'Mobile' | 'Web';
  os: string;
  initOptions?: Record<string, unknown>;
  onApiKeyMissing?: () => void;
}

export function createUseAnalytics(config: AnalyticsFactoryConfig) {
  const {
    client,
    resolveApiKey,
    resolveEnv,
    platform,
    os,
    initOptions,
    onApiKeyMissing,
  } = config;

  return function useAnalytics(): AnalyticsHook {
    const session = useAppSelector(selectSession);

    const baseProps = (): EventProperties => ({
      platform,
      OS: os,
      env: resolveEnv(),
    });

    function initAnalytics() {
      const key = resolveApiKey();
      if (!key) {
        onApiKeyMissing?.();
        return;
      }
      client.init(
        key,
        session.isAuthenticated ? session.user?.uuid : undefined,
        {
          sessionTimeout: SESSION_TIMEOUT_MS,
          ...initOptions,
        }
      );
    }

    function updateUserAnalytics(user: UserResource) {
      client.setUserId(user.uuid);
      const id = new client.Identify();
      id.set('email', user.email);
      id.set('first_name', user.first_name);
      id.set('last_name', user.last_name);
      if (user.profile?.school_name) {
        id.set('school', user.profile.school_name);
      }
      client.identify(id);
    }

    function trackEvent(eventName: string, properties?: EventProperties) {
      client.track(eventName, {
        ...baseProps(),
        context: eventName.replace(/\s+/g, ''),
        ...properties,
      });
    }

    function trackClick(context: string, properties?: ClickProperties) {
      client.track('Click', {
        ...baseProps(),
        context,
        ...properties,
      });
    }

    function trackPageView(properties?: PageViewProperties) {
      client.track('Page View', {
        ...baseProps(),
        context: 'PageView',
        ...properties,
      });
    }

    async function flush() {
      try {
        const result = client.flush() as
          | Promise<unknown>
          | { promise?: Promise<unknown> }
          | undefined;
        if (!result) return;
        if ('promise' in result && result.promise) {
          await result.promise;
        } else if (typeof (result as Promise<unknown>).then === 'function') {
          await result;
        }
      } catch {
        // ignore flush errors
      }
    }

    async function logout() {
      client.track('Logout', { ...baseProps(), context: 'Logout' });
      await flush();
      client.reset();
    }

    return {
      initAnalytics,
      trackEvent,
      trackClick,
      trackPageView,
      updateUserAnalytics,
      flush,
      logout,
    };
  };
}
